import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { BRANDS, type Brand, type BrandId } from "./brands";
import { clearSessionCache, getSession, loadScope, saveScope, signOutPortal } from "./portalSession";

/**
 * Portal session state (v3 Displet auth boundary). Authorization is
 * SERVER-authoritative: `authed` is derived from the Worker's signed session
 * cookie via `getSession()`, never from client storage. Brand/space are UI
 * preferences (which space you're reading) persisted locally. The three sign-in
 * paths resolve to a Displet-minted JWT the Worker verifies (API_MAP.md); the
 * live exchange flips on when the Displet secrets are provisioned.
 */

export interface PortalUser {
  name: string;
  initials: string;
  org: string;
}

interface PortalState {
  brand: Brand;
  brandId: BrandId;
  setBrand: (id: BrandId) => void;
  spaceId: string;
  setSpace: (id: string) => void;
  status: "loading" | "authed" | "anon";
  authed: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  user: PortalUser;
}

const Ctx = createContext<PortalState | null>(null);

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  return (parts[0]![0]! + (parts.length > 1 ? parts[parts.length - 1]![0]! : "")).toUpperCase();
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const scope = loadScope();
  const [brandId, setBrandIdState] = useState<BrandId>(scope.brandId);
  const [spaceId, setSpaceIdState] = useState<string>(scope.spaceId);
  const [status, setStatus] = useState<"loading" | "authed" | "anon">("loading");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");

  const refresh = useCallback(async () => {
    const s = await getSession(true);
    setStatus(s.authed ? "authed" : "anon");
    setName(s.name ?? "");
    setOrg(s.org ?? "");
  }, []);

  // Hydrate auth from the server on mount (the cookie is httpOnly — the only way
  // to know). Also re-check when the tab regains focus, so a server-side expiry or
  // sign-out elsewhere is reflected.
  useEffect(() => {
    let alive = true;
    getSession().then((s) => {
      if (!alive) return;
      setStatus(s.authed ? "authed" : "anon");
      setName(s.name ?? "");
      setOrg(s.org ?? "");
    });
    return () => {
      alive = false;
    };
  }, []);

  const setBrand = useCallback((id: BrandId) => {
    setBrandIdState(id);
    setSpaceIdState("platform"); // switching brand resets to that brand's platform docs
    saveScope({ brandId: id, spaceId: "platform" });
  }, []);

  const setSpace = useCallback(
    (id: string) => {
      setSpaceIdState(id);
      saveScope({ brandId, spaceId: id });
    },
    [brandId],
  );

  const signOut = useCallback(async () => {
    await signOutPortal();
    clearSessionCache();
    setStatus("anon");
    setName("");
    setOrg("");
  }, []);

  const safeBrandId: BrandId = BRANDS[brandId] ? brandId : "recursive";

  const value = useMemo<PortalState>(
    () => ({
      brand: BRANDS[safeBrandId],
      brandId: safeBrandId,
      setBrand,
      spaceId,
      setSpace,
      status,
      authed: status === "authed",
      refresh,
      signOut,
      // org falls back to the brand name until the network label is known.
      user: { name, initials: initials(name), org: org || BRANDS[safeBrandId].name },
    }),
    [safeBrandId, spaceId, status, name, org, setBrand, setSpace, refresh, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePortal(): PortalState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePortal must be used within a PortalProvider");
  return ctx;
}
